/**
 * ImpersonationControl.jsx
 *
 * "Assume identity" — a Supervisor+ troubleshooting tool to view the app
 * exactly as another user: the full interactive UI (composer, wizard, edit
 * controls all render as they do for that user), with every persisting action
 * blocked — the API rejects writes, and the finishing flows (post comment /
 * submit case) short-circuit with a notice. Two pieces:
 *   - AssumeIdentityDialog: enter an email; the server verifies the user exists
 *     and resolves their ACTUAL role (never picked here) before assuming.
 *   - ImpersonationBanner:   persistent bar with an always-available Exit.
 *
 * Apply/exit trigger a full reload so every page refetches with the new
 * X-Impersonate-* headers; the target persists in sessionStorage across it.
 * The Exit route is driven by the *real* Supervisor/SuperAdmin and clears local state only,
 * so it can never be locked out by whatever role is assumed.
 */

import React, { useState } from 'react';
import { useAuth, ROLE_LABELS, resolveRolePriority, canAssumeRole, impersonationScopeAnchor } from '../auth/AuthContext.jsx';
import { useAppConfig } from '../contexts/AppConfigContext.jsx';
import { apiFetch } from '../api/apiFetch.js';
import StaffEmailInput from './StaffEmailInput.jsx';

function callerPriority(user, config) {
  const role = user?.isSuperAdmin ? 'SuperAdmin' : user?.role;
  const def = (config?.roleDefinitions || []).find(d => d?.value === role);
  return resolveRolePriority(def || { value: role });
}

// Display label for an assumed role — built-in label, else the custom role's.
function roleLabel(config, role) {
  if (ROLE_LABELS[role]) return ROLE_LABELS[role];
  const def = (config?.roleDefinitions || []).find(r => r?.value === role);
  return def?.label || role;
}

export function AssumeIdentityDialog({ onClose }) {
  const { user, setImpersonation, isImpersonating, impersonation } = useAuth();
  const { config } = useAppConfig();
  const prio = callerPriority(user, config);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy]   = useState(false);

  async function assume() {
    const e = email.trim().toLowerCase();
    if (!e || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) {
      setError('Enter a valid email address.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      // Resolve who this actually is — the server checks the user exists in the
      // directory and returns THEIR role; nothing is picked here.
      const target = await apiFetch(`/api/access/impersonation-target?email=${encodeURIComponent(e)}`);
      const role = target.role;
      const customDef = (config?.roleDefinitions || []).find(r => r?.value === role && r.builtIn === false);
      if (!canAssumeRole(prio, role, customDef)) {
        setError(`${e} is ${target.roleLabel || role} — you can only assume lower-privilege users.`);
        return;
      }
      const scopeAnchor = isImpersonating ? impersonationScopeAnchor(impersonation) : null;
      const probeHeaders = { 'X-Impersonate-Role': role, 'X-Impersonate-Email': e };
      if (scopeAnchor) {
        probeHeaders['X-Impersonate-Scope-Email'] = scopeAnchor.email;
        probeHeaders['X-Impersonate-Scope-Role']  = scopeAnchor.role;
      }
      await apiFetch('/api/cases?limit=1', { headers: probeHeaders });
      setImpersonation({
        role,
        email: e,
        ...(scopeAnchor ? { scopeAnchor } : {}),
      });
      window.location.assign('/cases');
    } catch (err) {
      const msg = String(err?.message || err).replace(/^Forbidden:\s*/i, '');
      setError(msg || 'Cannot assume that identity.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Assume identity"
      style={overlayStyle}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={dialogStyle}>
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>
          {isImpersonating ? 'Switch identity' : 'Assume identity'}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-3, #888)', marginBottom: 16 }}>
          {isImpersonating
            ? `Pick another user within ${impersonation?.scopeAnchor?.email || impersonation?.email}'s scope — your signed-in permissions still apply, but region/clinic limits follow who you are viewing as.`
            : 'View the app exactly as another user — everything they can see and open, scoped to their clinic / region and capped by your own scope. Nothing can be saved or posted while assuming: finishing an action shows a notice instead.'}
        </div>

        <label style={labelStyle}>User email</label>
        <StaffEmailInput
          value={email}
          onChange={(v) => { setEmail(v); setError(''); }}
          onEnter={assume}
          autoFocus
          inputClass=""
          selectClass=""
          inputStyle={inputStyle}
          selectStyle={{ ...inputStyle, width: 'auto' }}
        />
        <div style={{ fontSize: 11, color: 'var(--text-3, #888)', marginTop: 6 }}>
          Must be an existing user — you'll view the app with their actual role
          and their clinic / region scope, intersected with your own.
        </div>

        {error && <div style={{ color: 'var(--danger, #c00)', fontSize: 12, marginTop: 10 }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <button className="btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn primary" onClick={assume} disabled={busy}>
            {busy ? 'Checking…' : 'Assume identity'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function ImpersonationBanner() {
  const { isImpersonating, impersonation, realUser, exitImpersonation, setImpersonationSubIdentity } = useAuth();
  const { config } = useAppConfig();
  if (!isImpersonating || !impersonation) return null;

  function exit() {
    exitImpersonation();
    window.location.assign('/cases');
  }

  // A clinic mailbox is viewed through ONE of its people (SubIdentityGate).
  // Dropping the person puts the picker back up — no reload: the gate renders in
  // place of the app, so every page remounts and refetches when one is chosen.
  const person = impersonation.role === 'ClinicMailbox' ? impersonation.subName : null;

  return (
    <div style={bannerStyle} role="status">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16, flexShrink: 0 }}>
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
      <span style={{ fontWeight: 600 }}>Viewing as {person || impersonation.email}</span>
      <span style={{ opacity: 0.85 }}>
        · {roleLabel(config, impersonation.role)}{person ? ` · ${impersonation.email}` : ''} · actions disabled
      </span>
      <span style={{ opacity: 0.7, marginLeft: 'auto', fontSize: 11 }}>
        signed in as {realUser?.email}
      </span>
      {person && (
        <button type="button" onClick={() => setImpersonationSubIdentity(null)} style={exitBtnStyle}>
          Change person
        </button>
      )}
      <button type="button" onClick={exit} style={exitBtnStyle}>Exit</button>
    </div>
  );
}

// ── styles ──────────────────────────────────────────────────────────────────
const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 400,
};
const dialogStyle = {
  background: 'var(--surface, #fff)', border: '1px solid var(--border, #e5e5e5)',
  borderRadius: 12, boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
  padding: 20, width: 'min(420px, calc(100vw - 32px))',
};
const labelStyle = {
  display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-2, #555)',
  marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.03em',
};
const inputStyle = {
  width: '100%', padding: '8px 10px', fontSize: 13, borderRadius: 8,
  border: '1px solid var(--border, #d4d4d4)', background: 'var(--surface, #fff)',
  color: 'var(--text-1, #111)', boxSizing: 'border-box',
};
const bannerStyle = {
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '8px 16px', fontSize: 12,
  background: '#7c3aed', color: '#fff',
  position: 'sticky', top: 0, zIndex: 120,
};
const exitBtnStyle = {
  background: 'rgba(255,255,255,0.18)', color: '#fff', border: '1px solid rgba(255,255,255,0.35)',
  borderRadius: 6, padding: '4px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
  marginLeft: 8,
};
